import { normalizeNewlines } from './promptDiff';
import { parseHeadings } from './promptMarkdown';

// サーバー側 api/api/libraries/utils/prompt_lint.py と同じ規則にすること
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;
const FOUR_BYTE = /[\u{10000}-\u{10FFFF}]/gu;
const FENCE_PATTERN = /^\s*```/;

// 1つの { } は変数として解釈されるため、文字として書きたい場合は {{ }} と2つ重ねるよう案内する
const VARIABLE_NOTE = '1つの { } は変数用です。';
const doubled = (detail) => '{' + detail + '}';

const TEMPLATE_MESSAGES = {
    unmatched_open: () => '{ を1つだけ書くことはできません（1つの { は変数の始まりとして扱われます）。文字として書く場合は {{ と2つ重ねてください',
    unmatched_close: () => '} を1つだけ書くことはできません（1つの } は変数の終わりとして扱われます）。文字として書く場合は }} と2つ重ねてください',
    invalid_field: (detail) => `${detail} は変数として扱えません。${VARIABLE_NOTE}文字として書く場合は ${doubled(detail)} のように2つ重ねてください`,
    format_spec: (detail) => `${detail} の書式指定（: や !）は使えません。${VARIABLE_NOTE}文字として書く場合は ${doubled(detail)} のように2つ重ねてください`,
    unknown_variable: (detail, allowed) => `${detail} は使用できない変数です（使える変数: ${allowed.map((v) => '{' + v + '}').join('・')}）。`
        + `${VARIABLE_NOTE}文字として書く場合は ${doubled(detail)} のように2つ重ねてください`,
};

const TEMPLATE_SHORTS = {
    unmatched_open: () => '単独の {（文字なら {{）',
    unmatched_close: () => '単独の }（文字なら }}）',
    invalid_field: (detail) => `変数名でない ${detail}（文字なら ${doubled(detail)}）`,
    format_spec: (detail) => `書式指定は使用不可（文字なら ${doubled(detail)}）`,
    unknown_variable: (detail) => `使用できない変数 ${detail}（文字なら ${doubled(detail)}）`,
};

/**
 * LangChain の from_template と同じ字句規則で、実行時エラーになる波括弧を検出する
 * @returns {Array<{kind, line, col, detail, message}>} line / col は1始まり
 */
export const checkTemplate = (text, allowedVariables) => {
    const src = normalizeNewlines(text);
    const allowedList = [...(allowedVariables || [])].sort();
    const allowed = new Set(allowedList);
    const issues = [];
    const issue = (kind, line, col, detail = '') => issues.push({
        kind, line, col, detail,
        message: TEMPLATE_MESSAGES[kind](detail, allowedList),
    });

    let line = 1;
    let col = 1;
    let i = 0;
    const n = src.length;
    const advance = (k) => {
        for (let c = 0; c < k; c++) {
            if (src[i] === '\n') { line += 1; col = 1; } else { col += 1; }
            i += 1;
        }
    };

    while (i < n) {
        const ch = src[i];
        if (ch === '{') {
            if (src[i + 1] === '{') { advance(2); continue; }
            // 同じ行の中で最初の } までを変数とみなす（改行や { を挟んだら閉じ忘れ）
            let j = i + 1;
            while (j < n && !'{}\n'.includes(src[j])) j += 1;
            if (j >= n || src[j] !== '}') {
                issue('unmatched_open', line, col);
                advance(1);
                continue;
            }
            const field = src.slice(i + 1, j);
            const name = field.split(/[!:]/)[0];
            if (!IDENTIFIER.test(name)) issue('invalid_field', line, col, `{${field}}`);
            else if (name !== field) issue('format_spec', line, col, `{${field}}`);
            else if (!allowed.has(name)) issue('unknown_variable', line, col, `{${name}}`);
            advance(j - i + 1);
            continue;
        }
        if (ch === '}') {
            if (src[i + 1] === '}') { advance(2); continue; }
            issue('unmatched_close', line, col);
        }
        advance(1);
    }
    return issues;
};

// utf8mb3 の列に保存できない4バイト文字を行・列（1始まり）付きで返す
export const find4byteChars = (text) => {
    const issues = [];
    normalizeNewlines(text).split('\n').forEach((ln, idx) => {
        let col = 1;
        for (const ch of ln) {
            if (ch.match(FOUR_BYTE)) issues.push({ line: idx + 1, col, char: ch });
            col += 1;
        }
    });
    return issues;
};

// ------------------------------------------------------------------
// PRizmo が自分で解釈する書き方の警告（Markdown としての正しさは見ない）
// ------------------------------------------------------------------

// グループ・タブごとに見る項目
// link: ウィジェットのリンク変換（共感・最終返答の出力） / img: 最終返答のユーザープロンプトの画像タグ
// conversationLog: 対話レポートのユーザープロンプトの会話ログ差し込み位置
const MARKUP_CHECKS = {
    empathy: { sys: ['link'], user: ['link'] },
    character: { sys: ['link'], user: ['link', 'img'] },
    report: { sys: [], user: ['conversationLog'] },
};

export const markupChecksOf = (groupKey, tab) => (MARKUP_CHECKS[groupKey] && MARKUP_CHECKS[groupKey][tab]) || [];

const CONVERSATION_LOG_TOKEN = '###CONVERSATION_LOG###';
const LINK_PATTERN = /\[([^\]\n]*)\]\(([^)\n]*)(\))?/g;
const FULLWIDTH_LINK_PATTERN = /[［[][^[\]［］\n]*[\]］][（(]/g;
const IMG_START_PATTERN = /@@(\s*)(img)(\s*)([（(])/gi;
const INQUIRY_URL = 'inquiry://open';

const MARKUP_MESSAGES = {
    link_unclosed: ['リンクの ) 閉じ忘れ', 'リンクの ) が閉じていません。AI がこのまま回答に使うと、ウィジェットでは後ろの文章までリンク先として扱われます'],
    link_empty_url: ['リンク先が空', 'リンク先が空です。[文字](https://…) の形で書いてください'],
    link_empty_text: ['リンクの文字が空', 'リンクの文字が空です。ウィジェットではリンクとして表示されません'],
    link_space: ['URL に空白', 'URL に空白が含まれているため、ウィジェットではリンクとして表示されません'],
    link_paren: ['URL 内の ( )', 'URL に ( ) が含まれていると、ウィジェットでは最初の ) でリンクが切れます'],
    link_inquiry: ['有人引き継ぎリンクの表記', '有人引き継ぎのリンクは [文字](inquiry://open) と正確に書いてください'],
    link_scheme: ['リンクにならないURL', 'ウィジェットでリンクになるのは http:// ・ https:// で始まるURLと inquiry://open だけです（mailto: や相対パスは文字のまま表示されます）'],
    link_fullwidth: ['全角の括弧', '全角の括弧ではリンクになりません。半角の [ ] ( ) で書いてください'],
    img_notation: ['画像タグの表記', '画像タグは @@img(https://…) と書いてください（@@・img・( の間に空白を入れず、img は小文字）'],
    img_fullwidth: ['全角の括弧', '全角の括弧では画像タグとして認識されません。@@img(https://…) と半角で書いてください'],
    img_unclosed: ['画像タグの ) 閉じ忘れ', '画像タグの ) が閉じていません'],
    img_empty: ['空の画像タグ', '画像タグの URL が空です'],
    img_space: ['URL に空白', 'URL に空白が含まれていると画像が表示されません'],
    img_paren: ['URL 内の ( )', 'URL に ( ) が含まれていると、最初の ) で画像タグが切れます'],
    img_scheme: ['画像URLは https://', '画像の URL は https:// で始めてください（http:// や相対パスは画像が表示されません）'],
    log_notation: ['###CONVERSATION_LOG### の表記', '会話ログの差し込み位置は ###CONVERSATION_LOG### と正確に書いてください（今の書き方では差し込まれず、会話ログはプロンプトの末尾に追加されます）'],
    log_missing: ['###CONVERSATION_LOG### が無い', '###CONVERSATION_LOG### がありません。会話ログはプロンプトの末尾に区切りなしで追加されます'],
};

const markupIssue = (line, kind) => ({ line, sev: 'warn', short: MARKUP_MESSAGES[kind][0], msg: MARKUP_MESSAGES[kind][1] });

// 1つのリンクの問題（最初の1件だけ）
const linkProblem = (text, url, closed) => {
    if (!closed) return 'link_unclosed';
    if (!url) return 'link_empty_url';
    if (!text) return 'link_empty_text';
    // [表示名](URL) のような書式の説明（: / . を含まない）は実際のリンクではないので見ない
    if (/^[^:/.]+$/.test(url)) return null;
    if (/\s/.test(url)) return 'link_space';
    if (url.includes('(')) return 'link_paren';
    if (url === INQUIRY_URL) return null;
    if (/^inquiry:/i.test(url)) return 'link_inquiry';
    if (!/^https?:\/\/\S+$/.test(url)) return 'link_scheme';
    return null;
};

const checkLinks = (ln, i, issues) => {
    for (const m of ln.matchAll(FULLWIDTH_LINK_PATTERN)) {
        if (/[［］（]/.test(m[0])) issues.push(markupIssue(i, 'link_fullwidth'));
    }
    for (const m of ln.matchAll(LINK_PATTERN)) {
        const kind = linkProblem(m[1], m[2], m[3] === ')');
        if (kind) issues.push(markupIssue(i, kind));
    }
};

const checkImages = (ln, i, issues) => {
    for (const m of ln.matchAll(IMG_START_PATTERN)) {
        if (m[1] || m[3] || m[2] !== 'img') { issues.push(markupIssue(i, 'img_notation')); continue; }
        if (m[4] === '（') { issues.push(markupIssue(i, 'img_fullwidth')); continue; }
        const start = m.index + m[0].length;
        const end = ln.indexOf(')', start);
        if (end < 0) { issues.push(markupIssue(i, 'img_unclosed')); continue; }
        const url = ln.slice(start, end);
        const kind = !url ? 'img_empty'
            : /\s/.test(url) ? 'img_space'
            : url.includes('(') ? 'img_paren'
            : !url.startsWith('https://') ? 'img_scheme' : null;
        if (kind) issues.push(markupIssue(i, kind));
    }
};

const checkConversationLog = (lines, issues) => {
    if (lines.some((ln) => ln.includes(CONVERSATION_LOG_TOKEN))) return;
    const similar = lines.findIndex((ln) => /#+\s*conversation[\s_-]*log\s*#*/i.test(ln));
    issues.push(similar >= 0 ? markupIssue(similar, 'log_notation') : markupIssue(0, 'log_missing'));
};

/**
 * PRizmo の機能が解釈する書き方（リンク・画像タグ・会話ログの差し込み位置）の警告
 * @param {string} text
 * @param {string[]} checks markupChecksOf() の値
 * @returns {Array<{line, sev:'warn', msg, short}>} line は0始まり
 */
export const lintMarkup = (text, checks) => {
    if (!checks || !checks.length) return [];
    const lines = normalizeNewlines(text).split('\n');
    const issues = [];
    lines.forEach((ln, i) => {
        if (checks.includes('link')) checkLinks(ln, i, issues);
        if (checks.includes('img')) checkImages(ln, i, issues);
    });
    if (checks.includes('conversationLog')) checkConversationLog(lines, issues);
    return issues;
};

/**
 * エディタ用の検査（デザインの規則 + サーバーから返る rules）
 * @param {string} text
 * @param {{template_variables: string[]|null, utf8mb3: boolean}} rule
 * @param {string[]} [markupChecks] PRizmo 固有の書き方の検査項目（markupChecksOf）
 * @returns {Array<{line, sev:'error'|'warn', msg, short}>} line は0始まり
 */
export const lintPrompt = (text, rule, markupChecks = []) => {
    const src = normalizeNewlines(text);
    const lines = src.split('\n');
    const issues = [];

    if (rule && rule.template_variables) {
        checkTemplate(src, rule.template_variables).forEach((it) => {
            issues.push({ line: it.line - 1, sev: 'error', msg: it.message, short: TEMPLATE_SHORTS[it.kind](it.detail) });
        });
    }
    if (rule && rule.utf8mb3) {
        find4byteChars(src).forEach((it) => {
            issues.push({ line: it.line - 1, sev: 'error', msg: `絵文字などの4バイト文字「${it.char}」は保存できません`, short: '保存できない文字 ' + it.char });
        });
    }

    issues.push(...lintMarkup(src, markupChecks));

    let fenceLine = -1;
    lines.forEach((ln, i) => {
        if (FENCE_PATTERN.test(ln)) fenceLine = fenceLine < 0 ? i : -1;
    });
    if (fenceLine >= 0) issues.push({ line: fenceLine, sev: 'warn', msg: 'コードブロック（```）が閉じられていません', short: '``` の閉じ忘れ' });

    // 長文でも入力が重くならないよう、見出しの検査は見出し数・行数に比例する計算量に抑える
    const heads = parseHeadings(src);
    const nonBlankBefore = [0];
    lines.forEach((l, i) => { nonBlankBefore[i + 1] = nonBlankBefore[i] + (l.trim() ? 1 : 0); });
    const addHead = (hd, msg, short) => issues.push({ line: hd.line, sev: 'warn', msg, short });
    const parents = [];
    const stack = [];
    heads.forEach((hd, k) => {
        if (!hd.text) addHead(hd, '見出しの文字がありません', '空の見出し');
        const prev = heads[k - 1];
        if (prev && hd.level > prev.level + 1) {
            addHead(hd, `見出しレベルが飛んでいます（H${prev.level} → H${hd.level}）。H${prev.level + 1} 以下にしてください`, `見出しレベル飛び H${prev.level} → H${hd.level}`);
        }
        // 親見出し（直前の上位レベル）
        while (stack.length && heads[stack[stack.length - 1]].level >= hd.level) stack.pop();
        parents[k] = stack.length ? stack[stack.length - 1] : -1;
        stack.push(k);
        // 直後の見出しが下位なら子あり。無ければ次の見出しまでに本文があるかを見る
        const next = heads[k + 1];
        const hasChild = !!next && next.level > hd.level;
        const endLine = next ? next.line : lines.length;
        const hasBody = nonBlankBefore[endLine] - nonBlankBefore[hd.line + 1] > 0;
        if (!hasChild && !hasBody) addHead(hd, '見出しの下に本文がありません', '中身のない見出し');
    });
    const keyOf = (hd, k) => hd.level + '|' + parents[k] + '|' + hd.text;
    const counts = new Map();
    heads.forEach((hd, k) => { if (hd.text) counts.set(keyOf(hd, k), (counts.get(keyOf(hd, k)) || 0) + 1); });
    heads.forEach((hd, k) => {
        if (hd.text && counts.get(keyOf(hd, k)) > 1) addHead(hd, `同じ階層に同じ名前の見出し「${hd.text}」があります`, '同名の見出し');
    });
    return issues;
};
